import { DateTime } from 'luxon';

/**
 * Frontend pre-fill defaults for the alert/reminder time-of-day picker.
 * Mirrors the backend `SCHEDULE_DEFAULTS` constant in `alertScheduleService.ts`
 * one-for-one — like `UNIT_DAY_COUNT`, changing a default value here means
 * updating both this file and the backend constant, nothing else. An alert
 * with no `atTime`/`timeZone` in its trigger config fires at the matching
 * default below; setting a picker value is what starts writing those fields.
 */
export const SCHEDULE_DEFAULTS = {
  recurringAlert: { atTime: '04:00', timeZone: 'America/Los_Angeles' },
  oneTime: { atTime: '08:00', timeZone: 'America/New_York' },
  reminder: { atTime: '08:00', timeZone: 'America/New_York' },
} as const;

export type ScheduleDefault =
  (typeof SCHEDULE_DEFAULTS)[keyof typeof SCHEDULE_DEFAULTS];

/**
 * Converts a wall time ('HH:mm') kept in a fixed zone into the equivalent UTC
 * instant for the given reference day. The time picker always displays in
 * device-local time, so this is how a fixed-zone default (or a previously
 * saved `atTime`/`timeZone`) gets rendered as the picker's pre-fill value.
 */
export const getWallTimeInstant = (
  atTime: string,
  timeZone: string,
  referenceDate: Date = new Date()
): string => {
  const [hour, minute] = atTime.split(':').map(Number);
  return DateTime.fromJSDate(referenceDate, { zone: timeZone })
    .set({ hour, minute, second: 0, millisecond: 0 })
    .toUTC()
    .toISO() as string;
};

/** Convenience wrapper over {@link getWallTimeInstant} for a schedule default. */
export const getScheduleDefaultInstant = (
  scheduleDefault: ScheduleDefault,
  referenceDate: Date = new Date()
): string =>
  getWallTimeInstant(
    scheduleDefault.atTime,
    scheduleDefault.timeZone,
    referenceDate
  );

/**
 * Reads a picker instant back as the device-local `atTime`/`timeZone` pair
 * the trigger config stores once the user has actually chosen a time.
 */
export const getDeviceAtTimeFromInstant = (
  isoInstant: string
): { atTime: string; timeZone: string } => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const atTime = DateTime.fromISO(isoInstant).setZone(timeZone).toFormat('HH:mm');
  return { atTime, timeZone };
};
